import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageCircle, Plus } from 'lucide-react';
import { useGql } from '@ah/design';
import { Header, PullToRefresh } from '@ah/design/mobile';
import type { Conversation } from '../shared/types.ts';
import { CONVERSATIONS_QUERY, CONV_UPDATED_SUB } from '../shared/queries.ts';
import {
  ConvRowAvatar,
  ConversationsSkeleton,
  convTitle,
  timeAgoShort,
} from './_parts.tsx';
import { NewChatSheet } from './NewChatSheet.tsx';

const ME_QUERY = /* GraphQL */ `query Me { me { id } }`;

export function ConversationsList() {
  const gql = useGql();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [sheetOpen, setSheetOpen] = useState(false);

  const { data: me } = useQuery({
    queryKey: ['me', 'id'],
    queryFn: () => gql.request<{ me: { id: string } }>(ME_QUERY),
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['chat', 'conversations'],
    queryFn: () => gql.request<{ conversations: Conversation[] }>(CONVERSATIONS_QUERY),
  });

  useEffect(() => {
    const unsub = gql.subscribe(CONV_UPDATED_SUB, {}, () => {
      qc.invalidateQueries({ queryKey: ['chat', 'conversations'] });
    });
    return () => unsub();
  }, [gql, qc]);

  const items = data?.conversations ?? [];
  const currentUserId = me?.me.id ?? '';

  return (
    <div className="fixed inset-0 flex flex-col bg-background">
      <Header
        title="Chat"
        backTo="/"
        trailing={
          <button
            type="button"
            aria-label="New message"
            onClick={() => setSheetOpen(true)}
            className="flex items-center justify-center w-9 h-9 rounded-lg text-foreground hover:bg-accent"
          >
            <Plus size={20} />
          </button>
        }
      />

      <PullToRefresh className="flex-1" onRefresh={() => refetch()}>
        {isLoading ? (
          <ConversationsSkeleton />
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
            <MessageCircle size={36} className="text-muted-foreground" />
            <p className="text-sm text-muted-foreground">No conversations yet</p>
            <button
              type="button"
              onClick={() => setSheetOpen(true)}
              className="mt-2 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium"
            >
              Start a chat
            </button>
          </div>
        ) : (
          <ul>
            {items.map((c) => {
              const title = convTitle(c, currentUserId);
              const preview = c.lastMessage
                ? `${c.lastMessage.author.id === currentUserId ? 'You: ' : ''}${c.lastMessage.content || (c.lastMessage.attachments?.length ? 'Attachment' : '')}`
                : 'No messages yet';
              const unread = c.unreadCount > 0;
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => navigate(`/chat/${c.id}`)}
                    className="w-full text-left flex items-center gap-3 px-4 py-3 border-b border-border active:bg-muted"
                  >
                    <ConvRowAvatar conv={c} currentUserId={currentUserId} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p
                          className={`text-base truncate ${unread ? 'font-semibold' : 'font-medium'}`}
                        >
                          {title}
                        </p>
                        {c.lastMessage && (
                          <span className="text-xs text-muted-foreground shrink-0">
                            {timeAgoShort(c.lastMessage.createdAt)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <p
                          className={`text-sm truncate flex-1 ${unread ? 'text-foreground' : 'text-muted-foreground'}`}
                        >
                          {preview}
                        </p>
                        {unread && (
                          <span className="min-w-[20px] h-5 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center px-1.5">
                            {c.unreadCount > 9 ? '9+' : c.unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </PullToRefresh>

      <NewChatSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        onStarted={(id) => {
          setSheetOpen(false);
          navigate(`/chat/${id}`);
        }}
      />
    </div>
  );
}
